import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { AgGridModule } from 'ag-grid-angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatPaginatorIntlCro } from './services/mat-paginator-Intl.provider';

import { MaterialModule } from 'src/material-module';
import { InterceptorsModule } from 'src/app/core/interceptors/interceptors.module';

// ✅ Import your own directives, pipes, components here
import { AutofocusDirective } from './directives/auto-focus.directive';
import { NumberFieldDirective } from './directives/number-field.directive';
import { NumberMaskDirective } from './directives/number-mask.directive';
import { LongPressDirective } from './directives/long-press.directive';
import { DragDirective } from './directives/drag-drop.directive';
import { LanguageDirectionDirective } from './directives/language-direction.directive';
import { ScanDetectDirective } from './directives/scan-detect.directive';
import { RowHighlightOnMenuOpenDirective } from './directives/row-highlight-on-menu-open.directive';
import { PositiveNumberDirective } from './directives/positive-number.directive';
import { InertWhenDirective } from './directives/inert-when.directive';
import { AgGridIdsDirective } from './directives/ag-grid-ids.directive';
import { DaterangepickerLocalDateDirective } from './directives/daterangepicker-local-date.directive';
import { PostHogDataTrackDirective } from './directives/posthog-dataTrack.directive';

import { CurrencyFormatter } from './pipes/currency-formatter.pipe';
import { QuantityFormatter } from './pipes/quantity-formatter.pipe';
import { DateFormatter } from './pipes/date-formatter.pipe';
import { MoneyRounding } from './pipes/money-rounding.pipe';
import { ToNumberPipe } from './pipes/string-to-number.pipe';
import { AuditLogValueFormatter } from './pipes/audit-log-value-formatter.pipe';
import { WorkflowAuditLogValueFormatter } from './pipes/workflow-audit-log-value-formatter';
import { HasPermissionPipe } from './pipes/has-permission.pipe';
import { HasPermissionsPipe } from './pipes/has-permissions.pipe';
import { IsInRolePipe } from './pipes/is-in-role.pipe';
import { DescriptiveErrorPipe } from './pipes/descriptive-error.pipe';
import { DomSanitizerPipe } from './pipes/dom-sanitizer.pipe';
import { AgoDatePipe } from './pipes/ago-date.pipe';
import { OrderByPipe } from './pipes/order-by.pipe';
import { CurrencyFormatterV3Pipe } from './pipes/currency-formatter-v3.pipe';
import { QuantityFormatterV3Pipe } from './pipes/quantity-formatter-v3.pipe';
import { ObjectToArrayPipe } from './pipes/object-to-array.pipe';
import { MultiplyPipe } from './pipes/multiply.pipe';
import { Capitalize } from './pipes/capitalize.pipe';
import { AgFilterColumnsPipe } from './pipes/ag-filter-columns.pipe';
import { AgFilterColumnOperatorsPipe } from './pipes/ag-filter-column-operators.pipe';
import { GetAgFilterColumnPipe } from './pipes/get-ag-filter-column.pipe';
import { FilterPipe } from './pipes/filter.pipe';
import { ListTranslatePipe } from './pipes/list-translate.pipe';
import { FindItemInArrayPipe } from './pipes/find-item-in-array.pipe';
import { HasAnyExtraAttributePipe } from './pipes/has-any-extra-attribute.pipe';
import { FromNowPipe } from './pipes/from-now.pipe';
import { AddressTranslatePipe } from './pipes/address-translate.pipe';

import { ToggleButtonComponent } from './components/toggle-button/toggle-button.component';
import { LoadingComponent } from './components/loading/loading.component';
import { UploadFileComponent } from './components/upload-file/upload-file.component';
import { AutoCompleteComponent } from './components/auto-complete/auto-complete.component';
import { AuditLogComponent } from './components/audit-log/audit-log.component';
import { LookupChipsComponent } from './components/lookup-chips/lookup-chips.component';
import { IntlTelInputComponent } from './components/intl-tel-input/intl-tel-input.component';
import { ScopedDialogComponent } from './components/scoped-dialog/scoped-dialog.component';
import { IconComponent } from './components/icon/icon.component';
import { LogoComponent } from '@core/components/logo/logo.component';
import { AutoCompleteListBoxComponent } from './components/auto-complete-list-box/auto-complete-list-box.component';
import { CodeConfigurationComponent } from './components/code-configuration/code-configuration.component';
import { CurrencyContainerComponent } from './components/currency-container/currency-container.component';
import { BarcodeComponent } from './components/barcode/barcode.component';
import { AgUserEmailChipRendererComponent } from './components/ag-user-email-chip-renderer/ag-user-email-chip-renderer.component';
import { LandingListItemComponent } from './components/landing-list-item/landing-list-item.component';
import { LandingPageComponent } from './components/landing-page/landing-page.component';
import { LoadingSnackbarComponent } from './components/loading-snackbar/loading-snackbar.component';
import { NoteAlertComponent } from './components/note-alert/note-alert.component';
import { AgGridHeaderComponent } from './components/ag-grid-header/ag-grid-header.component';
import { AgGridCommonFilterViewComponent } from './components/ag-grid-common-filter-view/ag-grid-common-filter-view.component';
import { AgGridCommonFilterComponent } from './components/ag-grid-common-filter/ag-grid-common-filter.component';
import { AgGridCommonSortComponent } from './components/ag-grid-common-sort/ag-grid-common-sort.component';
import { AgGridCommonFieldsComponent } from './components/ag-grid-common-fields/ag-grid-common-fields.component';
import { ShareResourceComponent } from './components/share-resource/share-resource.component';
import { AgGridNoViewsAvailableComponent } from './components/ag-grid-no-views-available/ag-grid-no-views-available.component';
import { FieldsConfigurationComponent } from './components/fields-configuration/fields-configuration.component';
import { UserProfileComponent } from './components/user-profile/user-profile.component';
import { AgGridIndexSelectionCellRendererComponent } from './components/ag-grid-index-selection-cell-renderer/ag-grid-index-selection-cell-renderer.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { AgNoRowsOverlayComponent } from './components/ag-no-rows-overlay/ag-no-rows-overlay.component';
import { AgLoadingOverlayComponent } from './components/ag-loading-overlay/ag-loading-overlay.component';
import { ModulesLandingControllerComponent } from './components/modules-landing-controller/modules-landing-controller.component';
import { HardDeleteConfirmationDialogComponent } from './components/hard-delete-confirmation-dialog/hard-delete-confirmation-dialog.component';
import { AddressTranslateCellRendererComponent } from './components/address-translate-cell-renderer/address-translate-cell-renderer.component';
import { StepperComponent } from './components/stepper/stepper.component';
import { ClippedTextCellRendererComponent } from './components/clipped-text-cell-renderer/clipped-text-cell-renderer.component';
import { ImageHoverViewerComponent } from './components/image-hover-viewer/image-hover-viewer.component';
import { ImageHoverPreviewDirective } from './components/image-hover-viewer/image-hover-preview.directive';
import { ItemListComponent } from './components/item-list/item-list.component';
import { ItemListRowComponent } from './components/item-list-row/item-list-row.component';
import { WebhooksLogoComponent } from './components/webhooks-logo/webhooks-logo.component';

const DECLARABLES = [
  // Components
  LoadingComponent,
  ConfirmationDialogComponent,
  ToggleButtonComponent,
  UploadFileComponent,
  AutoCompleteComponent,
  AuditLogComponent,
  LookupChipsComponent,
  IntlTelInputComponent,
  ScopedDialogComponent,
  IconComponent,
  LogoComponent,
  SpinnerComponent,
  AgNoRowsOverlayComponent,
  AgLoadingOverlayComponent,
  AutoCompleteListBoxComponent,
  CodeConfigurationComponent,
  CurrencyContainerComponent,
  BarcodeComponent,
  AgUserEmailChipRendererComponent,
  LandingListItemComponent,
  ModulesLandingControllerComponent,
  LandingPageComponent,
  LoadingSnackbarComponent,
  NoteAlertComponent,
  AgGridHeaderComponent,
  AgGridCommonFilterViewComponent,
  AgGridCommonFilterComponent,
  AgGridCommonSortComponent,
  AgGridCommonFieldsComponent,
  HardDeleteConfirmationDialogComponent,
  UserProfileComponent,
  ShareResourceComponent,
  AgGridNoViewsAvailableComponent,
  FieldsConfigurationComponent,
  AgGridIndexSelectionCellRendererComponent,
  AddressTranslateCellRendererComponent,
  StepperComponent,
  ClippedTextCellRendererComponent,
  ImageHoverViewerComponent,
  ItemListComponent,
  ItemListRowComponent,
  WebhooksLogoComponent,

  // Directives
  AutofocusDirective,
  NumberFieldDirective,
  NumberMaskDirective,
  LongPressDirective,
  DragDirective,
  PositiveNumberDirective,
  InertWhenDirective,
  LanguageDirectionDirective,
  ScanDetectDirective,
  RowHighlightOnMenuOpenDirective,
  AgGridIdsDirective,
  PostHogDataTrackDirective,
  ImageHoverPreviewDirective,
  DaterangepickerLocalDateDirective,

  // Pipes
  CurrencyFormatter,
  QuantityFormatter,
  DateFormatter,
  MoneyRounding,
  ToNumberPipe,
  AuditLogValueFormatter,
  WorkflowAuditLogValueFormatter,
  HasPermissionPipe,
  HasPermissionsPipe,
  IsInRolePipe,
  DescriptiveErrorPipe,
  DomSanitizerPipe,
  AgoDatePipe,
  OrderByPipe,
  CurrencyFormatterV3Pipe,
  QuantityFormatterV3Pipe,
  ObjectToArrayPipe,
  MultiplyPipe,
  Capitalize,
  FromNowPipe,
  FilterPipe,
  FindItemInArrayPipe,
  ListTranslatePipe,
  AgFilterColumnsPipe,
  AgFilterColumnOperatorsPipe,
  GetAgFilterColumnPipe,
  HasAnyExtraAttributePipe,
  AddressTranslatePipe
];

@NgModule({
  declarations: [...DECLARABLES],
  imports: [
    CommonModule,
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    AgGridModule,
    TranslateModule,
    NgbModule,
    InterceptorsModule,
    RouterModule
  ],
  exports: [...DECLARABLES],
  providers: [
    HasPermissionsPipe,
    IsInRolePipe,
    FromNowPipe,
    AddressTranslatePipe,
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: MatPaginatorIntl,
      useFactory: (translate: TranslateService) => {
        const service = new MatPaginatorIntlCro();
        service.injectTranslateService(translate);
        return service;
      },
      deps: [TranslateService]
    }
  ]
})
export class SharedModule { }
